'use client';

import { useEffect, useState } from 'react';

// Third-party UI fixed to the viewport bottom that the bar must sit above:
// OneTrust banner, OneTrust post-consent settings button, Salesforce chat bar.
const BOTTOM_FIXED_SELECTORS = ['#onetrust-banner-sdk', '.ot-floating-button', '.search-container'];

export const STICKY_BOTTOM_OFFSET_VAR = '--sticky-pdp-bottom-offset';

function isVisibleBottomFixed(element: Element, viewportHeight: number): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.height > 0 &&
    rect.width > 0 &&
    // Full-screen overlays start above the viewport and must not count.
    rect.top >= 0 &&
    rect.bottom >= viewportHeight - 4
  );
}

// Tallest widget, not the sum: they are all bottom-anchored and overlap each other.
function measureBottomFixedOffset(): number {
  const viewportHeight = window.innerHeight;
  let topMost = viewportHeight;

  for (const selector of BOTTOM_FIXED_SELECTORS) {
    for (const element of document.querySelectorAll(selector)) {
      if (isVisibleBottomFixed(element, viewportHeight)) {
        topMost = Math.min(topMost, element.getBoundingClientRect().top);
      }
    }
  }

  return Math.min(viewportHeight, Math.max(0, Math.round(viewportHeight - topMost)));
}

/**
 * Height of the tallest bottom-fixed third-party widget on screen, mirrored to
 * `--sticky-pdp-bottom-offset` on <html> so the bar stacks above it. Measures
 * only while `enabled`.
 */
export function useBottomFixedOffset(enabled: boolean): number {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setOffset(0);
      return;
    }

    let frame = 0;
    const measure = () => {
      const next = measureBottomFixedOffset();
      setOffset((previous) => (previous === next ? previous : next));
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    schedule();

    // Widgets are script-injected late; transform slide-ins only surface on transitionend.
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('transitionend', schedule, true);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      document.removeEventListener('transitionend', schedule, true);
    };
  }, [enabled]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(STICKY_BOTTOM_OFFSET_VAR, `${offset}px`);
    return () => {
      root.style.removeProperty(STICKY_BOTTOM_OFFSET_VAR);
    };
  }, [offset]);

  return offset;
}
